import { t } from "@lingui/macro";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Suspense, useCallback, useEffect } from "react";
import { useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";

import { AuthenticationLayout } from "../../features/Authentication/components/AuthenticationLayout";
import { UnauthenticatedNavigatorParamList } from "../../navigators/UnauthenticatedStackNavigator/types";
import { UnauthenticatedRoutes } from "../../navigators/UnauthenticatedStackNavigator/unauthenticatedRoutes";
import { RegisterAccountContainerQueryString } from "../Register/components/RegisterAccountContainer";
import { RegisterAccountContainerQuery } from "../Register/components/__generated__/RegisterAccountContainerQuery.graphql";
import { ResetPasswordContainer } from "./components/ResetPasswordContainer";
import { setResetPasswordClubContext } from "./setResetPasswordClubContext";

export const ResetPassword = ({
  route,
  navigation,
}: NativeStackScreenProps<
  UnauthenticatedNavigatorParamList,
  "ResetPassword"
>) => {
  const [queryRef, loadQuery] = useQueryLoader<RegisterAccountContainerQuery>(
    RegisterAccountContainerQueryString
  );

  const { clubSlug, token } = route.params;

  useEffect(() => {
    let isCancelled = false;

    const loadResetPassword = async () => {
      await setResetPasswordClubContext(clubSlug);

      if (!isCancelled && token) {
        loadQuery({ token, userContext: clubSlug });
      }
    };

    void loadResetPassword();

    return () => {
      isCancelled = true;
    };
  }, [clubSlug, loadQuery, token]);

  const handleBadToken = useCallback(() => {
    navigation.navigate("Initial", {
      showErrorBanner: true,
      errorMessage: t`Invalid link. Login or Reset Password again.`,
    });
  }, [navigation]);

  const onComplete = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate(UnauthenticatedRoutes.Initial as never);
  };

  return (
    <AuthenticationLayout>
      <Suspense fallback={<Loader />}>
        {queryRef ? (
          <ResetPasswordContainer
            token={token}
            queryRef={queryRef}
            onComplete={onComplete}
            handleBadToken={handleBadToken}
          />
        ) : null}
      </Suspense>
    </AuthenticationLayout>
  );
};
